import { usePermissions } from '@my/directory-api/hooks';
import { Loading } from '@my/ui';
import { Navigate, Outlet } from 'react-router';

export interface RequireCapabilityProps {
  /**
   * Capabilities that admit the caller. Any one is enough — a group of routes
   * behind a single guard usually wants "you can reach at least one of these".
   */
  anyOf: string[];
  /**
   * Where to send someone who cannot be here. Defaults to the dashboard.
   *
   * <p>
   * Note it is not guaranteed: `dashboard` is itself a capability now, so a
   * user without it lands on a page that redirects again. The fallback below
   * handles that rather than looping.
   * </p>
   */
  redirectTo?: string;
}

/**
 * Route guard that asks about CAPABILITIES rather than roles.
 *
 * <p>
 * Replaces `RequireRole` for the settings tree. The role version could only
 * express "one of these roles", so "a Manager may reach two of these seven
 * pages" meant splitting the tree and repeating role literals — and every
 * future variation meant editing them again. A capability guard reads the same
 * server-computed list the menu does, so a page cannot be reachable while its
 * menu item is hidden, or the reverse.
 * </p>
 *
 * <p>
 * WAITS rather than guessing. The capability list arrives from the API, so for
 * the first moments it is unknown; redirecting then would bounce a legitimate
 * admin off their own settings page on every cold load. Rendering the outlet
 * then would flash a page they may not be allowed to see. So it shows a
 * loader — the only honest third option.
 * </p>
 *
 * <p>
 * Defence in depth, not the defence. The API enforces the platform role on
 * every call; this exists so a user who deep-links does not see a half-rendered
 * page that 403s on every request.
 * </p>
 */
export function RequireCapability({ anyOf, redirectTo = '/dashboard' }: RequireCapabilityProps) {
  const { can, isLoading, error } = usePermissions();

  if (isLoading) return <Loading label="Checking access" />;

  // A profile that could not be loaded is not a licence to proceed, but it is
  // also not proof of anything — send them somewhere harmless rather than
  // rendering a page whose data will fail too.
  if (error) return <Navigate to={redirectTo} replace />;

  if (!anyOf.some((capability) => can(capability))) {
    // `replace` so the disallowed URL does not sit in history — back after the
    // redirect goes where the user actually was.
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
